import React from 'react';
import { useQuery } from 'react-query';

import { Table } from 'antd';
import styled from 'styled-components';

import { getAlertPricing } from 'apis/AlertsAPI';
import ErrorMessage from 'components/lib/ErrorMessage';
import { AlertPricingRoomType, AlertPricingType } from 'types/alerts';
import { formatPrice } from 'utils/helpers/numbers';

import { buildRows, getDifferenceLabel, type PricingRow } from './utils';

type Props = {
  alertId: number;
};

type DifferenceLabel = {
  color: string;
  text: string;
};

const StyledTable = styled(Table)`
  &&& {
    .ant-table {
      border: none;
      border-radius: 0;
    }

    .ant-table-container {
      border: none !important;
    }

    .ant-table-thead > tr > th {
      font-family: var(--font-bold);
      text-transform: uppercase;
      font-size: 12px;
      letter-spacing: 0.5px;
      color: var(--text-primary);
      background: var(--white);
      border: none !important;
      border-bottom: none !important;
      border-inline-end: none !important;
      padding: 12px 16px;
    }

    .ant-table-thead > tr > th.ant-table-cell::before {
      display: none;
    }

    .ant-table-tbody > tr > td {
      padding: 10px 16px;
      border-bottom: 1px solid var(--line-gray);
      vertical-align: middle;
      background: var(--white);
      min-height: 60px;
    }

    .ant-table-tbody > tr > td:first-child {
      border-left: 1px solid var(--line-gray);
    }

    .ant-table-tbody > tr > td:last-child {
      border-right: 1px solid var(--line-gray);
    }

    .ant-table-tbody > tr:first-child > td {
      border-top: 1px solid var(--line-gray);
    }

    .ant-table-tbody > tr.my-community-row > td {
      background: rgba(231, 252, 235, 0.5); !important;
    }
  }
`;

const Title = styled.div`
  margin-bottom: 14px;
  font-size: 34px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const CommunityCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CommunityName = styled.span`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const CommunityType = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const PriceText = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const DifferenceText = styled.span<{ $color: string }>`
  font-size: 12px;
  color: ${({ $color }) => $color};
`;

const getPriceText = (value: number | null) => (value === null ? '-' : formatPrice(value, '$', 0, 0));

type PricingTableProps = {
  pricingData?: AlertPricingType;
  loading?: boolean;
};

export const PricingTable = ({ pricingData, loading }: PricingTableProps) => {
  const { roomTypes, rows } = pricingData
    ? buildRows(pricingData)
    : { roomTypes: [] as AlertPricingRoomType[], rows: [] as PricingRow[] };

  const columns = [
    {
      title: 'Community',
      key: 'community',
      width: '26%',
      render: (_value: unknown, record: PricingRow) => (
        <CommunityCell>
          <CommunityName>{record.communityName}</CommunityName>
          <CommunityType>{record.isMyCommunity ? 'My Community' : 'Competitor'}</CommunityType>
        </CommunityCell>
      ),
    },
    ...roomTypes.map((roomType) => ({
      title: roomType.room_type,
      key: `room-type-${roomType.room_type_id}`,
      render: (_value: unknown, record: PricingRow) => {
        const cell = record.roomTypeValues[roomType.room_type_id] || { marketRate: null, difference: null };
        const priceText = getPriceText(cell.marketRate);
        const differenceLabel: DifferenceLabel | null = getDifferenceLabel(cell.difference, cell.marketRate);

        return (
          <CellStack>
            <PriceText>{priceText}</PriceText>
            {!record.isMyCommunity && differenceLabel && (
              <DifferenceText $color={differenceLabel.color}>{differenceLabel.text}</DifferenceText>
            )}
          </CellStack>
        );
      },
    })),
  ];

  return (
    <div data-test-id="alerts-pricing-table">
      {!loading && pricingData && <Title>{pricingData.care_type || 'Pricing'} (Minimum Prices)</Title>}
      {pricingData && (
        <StyledTable
          columns={columns as any}
          dataSource={rows}
          loading={loading}
          rowKey={(record: any) => record.key}
          rowClassName={(record: any) => (record.isMyCommunity ? 'my-community-row' : '')}
          pagination={false}
          showSorterTooltip={false}
        />
      )}
    </div>
  );
};

const AlertsPricingTable = ({ alertId }: Props) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['alert-details-pricing', alertId],
    queryFn: () => getAlertPricing(alertId),
    enabled: Boolean(alertId),
    refetchOnWindowFocus: false,
  });

  return (
    <>
      <ErrorMessage text="Failed to load pricing details. Please try again." active={isError} />
      {!isError && <PricingTable pricingData={data as AlertPricingType | undefined} loading={isLoading} />}
    </>
  );
};

export default AlertsPricingTable;
